import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../components/common/DataTable';
import { PageHeader } from '../components/common/PageHeader';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MoreHorizontal, Eye, Edit, Calendar, Plus, Loader2, Trophy, UserCog, Users, Search } from 'lucide-react';
import { formatDate, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function EventsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('admin');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  
  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    event_start: '',
    event_end: '',
    registration_start: '',
    registration_end: '',
    // For user events - score update
    score_update: { match_id: '', score_a: '', score_b: '' },
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (search) params.append('search', search);
      if (status !== 'all') params.append('status', status);
      
      const response = await axios.get(`${API_URL}/api/events?${params}`);
      let allEvents = response.data.events || [];
      
      // Filter based on active tab
      if (activeTab === 'admin') {
        allEvents = allEvents.filter(e => e.created_by_admin === true || e.is_official === true);
      } else {
        allEvents = allEvents.filter(e => !e.created_by_admin && !e.is_official);
      }
      
      setEvents(allEvents);
      setPagination(prev => ({
        ...prev,
        total: allEvents.length,
        totalPages: Math.ceil(allEvents.length / pagination.limit) || 1,
      }));
    } catch (error) {
      toast.error('Failed to load events');
      setEvents([]);
      setPagination(prev => ({ ...prev, total: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, status, activeTab]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleEdit = (event) => {
    setEditingEvent(event);
    setEditForm({
      event_start: event.event_start || '',
      event_end: event.event_end || '',
      registration_start: event.registration_start || '',
      registration_end: event.registration_end || '',
      score_update: { match_id: '', score_a: '', score_b: '' },
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEvent) return;
    
    setSaving(true);
    try {
      const isAdminEvent = editingEvent.created_by_admin || editingEvent.is_official;
      
      // For admin events - only allow date changes
      // For user events - allow date changes and score updates
      const updateData = {
        event_start: editForm.event_start,
        event_end: editForm.event_end,
        registration_start: editForm.registration_start,
        registration_end: editForm.registration_end,
      };
      
      await axios.put(`${API_URL}/api/admin/challenges/${editingEvent.id}`, updateData);
      toast.success('Event updated successfully');
      setShowEditModal(false);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'event',
      header: 'Event',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-purple-500/20 flex items-center justify-center">
            {row.banner_image ? (
              <img src={row.banner_image} alt="" className="w-10 h-10 rounded-sm object-cover" />
            ) : (
              <Trophy className="w-5 h-5 text-purple-400" />
            )}
          </div>
          <div>
            <p className="font-medium text-white">{row.name || 'Unnamed Event'}</p>
            <p className="text-xs text-slate-500">{row.sport || row.sport_format} • {row.format_type || row.tournament_type || 'Tournament'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'scope',
      header: 'Scope',
      render: (row) => (
        <span className="text-slate-300 capitalize">{row.scope || 'Open'}</span>
      ),
    },
    {
      key: 'dates',
      header: 'Event Dates',
      render: (row) => (
        <div className="text-sm">
          <p className="text-slate-300">{row.event_start ? formatDate(row.event_start) : 'Not set'}</p>
          <p className="text-slate-500 text-xs">to {row.event_end ? formatDate(row.event_end) : 'Not set'}</p>
        </div>
      ),
    },
    {
      key: 'teams',
      header: 'Teams',
      render: (row) => (
        <span className="text-slate-300">{row.registered_teams || 0} / {row.max_teams || '-'}</span>
      ),
    },
    {
      key: 'entry_fee',
      header: 'Total Fee',
      render: (row) => (
        <span className="text-slate-300">
          {row.is_free ? 'Free' : `₹${row.final_fees_total || row.entry_fee || 0}`}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'created_by',
      header: 'Created By',
      render: (row) => (
        <div className="flex items-center gap-2">
          {(row.created_by_admin || row.is_official) ? (
            <>
              <UserCog className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-xs">Admin</span>
            </>
          ) : (
            <>
              <Users className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-xs">User</span>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`event-actions-${row.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
            {(row.created_by_admin || row.is_official) ? (
              <>
                <DropdownMenuItem 
                  onClick={() => navigate(`/events/${row.id}/view`)}
                  className="text-slate-300 focus:bg-slate-800 focus:text-white"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate(`/events/${row.id}?mode=edit`)}
                  className="text-slate-300 focus:bg-slate-800 focus:text-white"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Event
                </DropdownMenuItem>
              </>
            ) : (
              <>
                <DropdownMenuItem 
                  onClick={() => navigate(`/events/${row.id}/view`)}
                  className="text-slate-300 focus:bg-slate-800 focus:text-white"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleEdit(row)}
                  className="text-slate-300 focus:bg-slate-800 focus:text-white"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Dates
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="events-page">
      <PageHeader
        title="Events & Challenges"
        description="Manage tournaments and events from Lunipin app"
        action={() => navigate('/events/create')}
        actionLabel="Create Challenge"
        actionIcon={Plus}
      />

      {/* Tabs for Admin vs User Created */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger 
            value="admin" 
            className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900"
          >
            <UserCog className="w-4 h-4 mr-2" />
            Admin Created
          </TabsTrigger>
          <TabsTrigger 
            value="user"
            className="data-[state=active]:bg-blue-500 data-[state=active]:text-white"
          >
            <Users className="w-4 h-4 mr-2" />
            User Created
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search events..."
            className="bg-slate-800 border-slate-700 pl-10"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="registration_open">Registration Open</SelectItem>
            <SelectItem value="registration_closed">Registration Closed</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => {
            setSearch('');
            setStatus('all');
          }}
          className="border-slate-700"
        >
          Reset
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={events}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        emptyMessage={`No ${activeTab === 'admin' ? 'admin-created' : 'user-created'} events found`}
      />

      {/* Edit Event Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-green-400" />
              Edit Event: {editingEvent?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Event Dates - Always editable */}
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Event Dates
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Event Start</Label>
                  <Input
                    type="datetime-local"
                    value={editForm.event_start}
                    onChange={(e) => setEditForm(prev => ({ ...prev, event_start: e.target.value }))}
                    className="bg-slate-800 border-slate-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Event End</Label>
                  <Input
                    type="datetime-local"
                    value={editForm.event_end}
                    onChange={(e) => setEditForm(prev => ({ ...prev, event_end: e.target.value }))}
                    className="bg-slate-800 border-slate-700 mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-slate-300">Registration Start</Label>
                  <Input
                    type="datetime-local"
                    value={editForm.registration_start}
                    onChange={(e) => setEditForm(prev => ({ ...prev, registration_start: e.target.value }))}
                    className="bg-slate-800 border-slate-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Registration End</Label>
                  <Input
                    type="datetime-local"
                    value={editForm.registration_end}
                    onChange={(e) => setEditForm(prev => ({ ...prev, registration_end: e.target.value }))}
                    className="bg-slate-800 border-slate-700 mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Entry Fee Notice - Only for admin events */}
            {(editingEvent?.created_by_admin || editingEvent?.is_official) && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-sm">
                  <strong>Note:</strong> Entry fees cannot be modified for admin-created events.
                </p>
              </div>
            )}
            
            {/* Score Update - Only for user events with completed matches */}
            {!editingEvent?.created_by_admin && !editingEvent?.is_official && (
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Update Match Score
                </h4>
                <p className="text-slate-400 text-sm mb-3">Use this to correct scores if there was an issue entering them after a match.</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-300">Match ID</Label>
                    <Input
                      value={editForm.score_update.match_id}
                      onChange={(e) => setEditForm(prev => ({ 
                        ...prev, 
                        score_update: { ...prev.score_update, match_id: e.target.value }
                      }))}
                      placeholder="Match ID"
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Team A Score</Label>
                    <Input
                      type="number"
                      value={editForm.score_update.score_a}
                      onChange={(e) => setEditForm(prev => ({ 
                        ...prev, 
                        score_update: { ...prev.score_update, score_a: e.target.value }
                      }))}
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Team B Score</Label>
                    <Input
                      type="number"
                      value={editForm.score_update.score_b}
                      onChange={(e) => setEditForm(prev => ({ 
                        ...prev, 
                        score_update: { ...prev.score_update, score_b: e.target.value }
                      }))}
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="border-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={saving}
                className="bg-green-500 hover:bg-green-600 text-slate-900"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
